import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { isCookiesBlocked, onCookieStatusChange } from "@/lib/cookieProbe";
import { useT } from "@/lib/i18n";

const DISMISS_KEY = "cibraxis_cookie_banner_dismissed_at";

/**
 * Warns the user when only the in-memory Bearer fallback is keeping them logged in.
 * Dismissable per-tab; if user reopens tab and cookies still blocked, banner reappears.
 */
export function CookieBanner() {
  const { t } = useT();
  const [blocked, setBlocked] = useState(isCookiesBlocked());
  const [dismissed, setDismissed] = useState(() => !!sessionStorage.getItem(DISMISS_KEY));

  useEffect(() => onCookieStatusChange(setBlocked), []);

  if (!blocked || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      className="border-b border-sev-medium/50 bg-sev-medium/10 text-sev-medium px-6 py-2 flex items-start gap-3"
      data-testid="cookie-blocked-banner"
      role="alert"
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-xs font-mono">
        <div className="font-semibold tracking-wide">{t("banner.cookies_blocked.title")}</div>
        <div className="text-muted-foreground mt-0.5">{t("banner.cookies_blocked.body")}</div>
      </div>
      <button
        onClick={dismiss}
        className="p-1 hover:bg-sev-medium/20 flex-shrink-0"
        data-testid="cookie-banner-dismiss"
        aria-label={t("banner.cookies_blocked.dismiss")}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
